import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import apiCalls from '../utils/apiCalls';
import utils from '../utils/utils';
import Loader from '../components/loader';
import ErrorMessage from '../components/errorMessage';
import { Route } from 'react-router';
import MarketplaceAdminDeliveryDayMarketplaces from './marketplaceAdminDeliveryDayMarketplaces';
import MarketplaceAdminDeliveryDayMarketplace from './marketplaceAdminDeliveryDayMarketplace';
import MarketplaceAdminSidebar from './marketplaceAdminSidebar';
import MarketplaceAdminView from './marketplaceAdminView';
import { getMarketplaces, setMarketplace } from './marketplacesActions';
import MarketplaceAdminForm from './marketplaceAdminForm';
import MarketplaceAdminMarketplaceItemsForm from './marketplaceAdminMarketplaceItemsForm';
import MarketplaceAdminItemForm from './marketplaceAdminItemForm';
import MarketplaceAdminItemBulkForm from './marketplaceAdminItemBulkForm';
import MarketplaceAdminBills from './marketplaceAdminBills';
import MarketplaceAdminPayoutReports from './marketplaceAdminPayoutReports';
import MarketplaceAdminItemCollectionForm from './marketplaceAdminItemCollectionForm';
import MarketplaceAdminItemCollections from './marketplaceAdminItemCollections';
import MarketplaceAdminCombinedOrderItems from './marketplaceAdminCombinedOrders';

const MarketplaceAdmin = ({ marketplaces, loading, error, setMarketplace, getMarketplaces, currentUser, match }) => {
  const [deliveryDayMarketplacesLoading, setDeliveryDayMarketplacesLoading] = useState(null);
  const [deliveryDayMarketplacesError, setDeliveryDayMarketplacesError] = useState(null);
  const [deliveryDayMarketplaces, setDeliveryDayMarketplaces] = useState([]);

  const [pastDeliveryDayMarketplacesLoading, setPastDeliveryDayMarketplacesLoading] = useState(null);
  const [pastDeliveryDayMarketplacesError, setPastDeliveryDayMarketplacesError] = useState(null);
  const [pastDeliveryDayMarketplaces, setPastDeliveryDayMarketplaces] = useState([]);

  useEffect(() => {
    if (!marketplaces) {
      getMarketplaces({ userId: currentUser.id });
      return;
    }

    const marketplace = marketplaces.find((marketplace) => marketplace.id.toString() === match.params.marketplaceId);

    if (marketplace) {
      setMarketplace(marketplace);
    }
  }, [marketplaces]);

  useEffect(() => {
    setDeliveryDayMarketplacesError(null);
    setDeliveryDayMarketplacesLoading(true);
    apiCalls
      .getDeliveryDayMarketplaces({
        showAllServiceAreas: true,
        showB2bB2c: true,
        marketplaceId: match.params.marketplaceId,
        sortBy: 'deliveryDateAsc',
        includeCombinedOrderItems: true,
      })
      .then((result) => {
        if (result.data && result.data.deliveryDayMarketplaces) {
          setDeliveryDayMarketplaces(result.data.deliveryDayMarketplaces);
          setDeliveryDayMarketplacesLoading(false);
        }
      })
      .catch((errors) => {
        setDeliveryDayMarketplacesError(utils.getErrorMessageFromResponse(errors));
        setDeliveryDayMarketplacesLoading(false);
      });
  }, []);

  useEffect(() => {
    setPastDeliveryDayMarketplacesError(null);
    setPastDeliveryDayMarketplacesLoading(true);
    apiCalls
      .getDeliveryDayMarketplaces({
        showAllServiceAreas: true,
        showB2bB2c: true,
        marketplaceId: match.params.marketplaceId,
        sortBy: 'deliveryDateDesc',
        onlyPast: true,
        includeCombinedOrderItems: true,
      })
      .then((result) => {
        if (result.data && result.data.deliveryDayMarketplaces) {
          setPastDeliveryDayMarketplaces(result.data.deliveryDayMarketplaces);
          setPastDeliveryDayMarketplacesLoading(false);
        }
      })
      .catch((errors) => {
        setPastDeliveryDayMarketplacesError(utils.getErrorMessageFromResponse(errors));
        setPastDeliveryDayMarketplacesLoading(false);
      });
  }, []);

  return (
    <React.Fragment>
      <MarketplaceAdminSidebar />

      <div className="marketplace-admin">
        <Loader show={loading} />
        <ErrorMessage error={error} />

        <Loader show={deliveryDayMarketplacesLoading || pastDeliveryDayMarketplacesLoading} />
        <ErrorMessage error={deliveryDayMarketplacesError} />
        <ErrorMessage error={pastDeliveryDayMarketplacesError} />

        <Route
          exact
          path={`${match.path}`}
          render={(props) => (
            <div>
              <h3>
                <FormattedMessage id={'marketplace.admin.delivery.days.header'} />
              </h3>
              <MarketplaceAdminDeliveryDayMarketplaces deliveryDayMarketplaces={deliveryDayMarketplaces} />

              <h3>
                <FormattedMessage id={'marketplace.admin.delivery.days.past.header'} />
              </h3>
              <MarketplaceAdminDeliveryDayMarketplaces deliveryDayMarketplaces={pastDeliveryDayMarketplaces} />
            </div>
          )}
        />

        <Route path={`${match.path}/view`} render={(props) => <MarketplaceAdminView {...props} />} />
        <Route path={`${match.path}/edit`} render={(props) => <MarketplaceAdminForm action="edit" {...props} />} />
        <Route
          path={`${match.path}/combinedOrderItems`}
          render={(props) => (
            <MarketplaceAdminCombinedOrderItems
              deliveryDayMarketplaces={deliveryDayMarketplaces}
              pastDeliveryDayMarketplaces={pastDeliveryDayMarketplaces}
              {...props}
            />
          )}
        />
        <Route
          path={`${match.path}/marketplaceItems`}
          render={(props) => <MarketplaceAdminMarketplaceItemsForm {...props} />}
        />
        <Route path={`${match.path}/bills`} render={(props) => <MarketplaceAdminBills {...props} />} />
        <Route path={`${match.path}/payoutReports`} render={(props) => <MarketplaceAdminPayoutReports {...props} />} />
        <Route
          path={`${match.path}/item/create`}
          render={(props) => <MarketplaceAdminItemForm action="create" {...props} />}
        />
        <Route path={`${match.path}/item/createBulk`} render={(props) => <MarketplaceAdminItemBulkForm {...props} />} />
        <Route
          path={`${match.path}/item/edit/:itemId`}
          render={(props) => <MarketplaceAdminItemForm action="edit" {...props} />}
        />
        <Route
          path={`${match.path}/itemCollections`}
          render={(props) => <MarketplaceAdminItemCollections {...props} />}
        />
        <Route
          path={`${match.path}/itemCollections/create`}
          render={(props) => <MarketplaceAdminItemCollectionForm action="create" {...props} />}
        />
        <Route
          path={`${match.path}/itemCollections/edit/:itemCollectionId`}
          render={(props) => <MarketplaceAdminItemCollectionForm action="edit" {...props} />}
        />
        <Route
          path={`${match.path}/delivery/:deliveryDayMarketplaceId`}
          render={(props) => (
            <MarketplaceAdminDeliveryDayMarketplace
              {...props}
              deliveryDayMarketplaces={deliveryDayMarketplaces.concat(pastDeliveryDayMarketplaces)}
            />
          )}
        />
      </div>
    </React.Fragment>
  );
};

MarketplaceAdmin.propTypes = {
  currentUser: PropTypes.object,
  marketplace: PropTypes.object,
  marketplaces: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

MarketplaceAdmin.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
    marketplaces: state.marketplaces.marketplaces,
    marketplace: state.marketplaces.marketplace,
    loading: state.marketplaces.loading,
    error: state.marketplaces.error,
  };
}

const mapDispatchToProps = {
  setMarketplace,
  getMarketplaces,
};

export default connect(mapStateToProps, mapDispatchToProps)(MarketplaceAdmin);
